import { useRef } from 'react';

import Accordion from 'components/Accordion';
import BackToTop from 'components/BackToTop';
import Grid, { Container } from 'components/Grid';
import OneTapScript from 'components/OneTapScript';
import Paper from 'components/Paper';
import ProductInfoCard from 'components/ProductInfoCard';
import SoftwareApplicationLD from 'components/SoftwareApplicationLD/SoftwareApplicationLD';
import SplashElement from 'components/SplashElement';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { ListingDetailProvider } from 'context/ListingDetailContext';
import { useUser } from 'hooks';
import useViewport from 'hooks/useViewport';

import ManageProductListing from '../components/ManageProductListing';
import RichContent from '../components/RichContent';
import RichContentTitle from '../components/RichContentTitle';
import TableOfContent from '../components/TableOfContent';

import classes from './ListingDetailRichContent.module.scss';

/** @type {import("./types").default} */
const ListingDetailRichContent = ({ listing, listingDetailRichContent }) => {
  const { up } = useViewport();
  const { sendEvent } = useEventContext();
  const isDesktop = up('md');
  const stickyContainerRef = useRef();
  const { isCypressTakingSnapshots, head, header } = useAppContext();
  const [user] = useUser();

  const handleTableOfContentExpand = (_, { isExpanded }) => {
    sendEvent(
      isExpanded
        ? {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDestination: 'open_table-of-content',
            engagementDetails: 'expand_table-of-content',
          }
        : {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDetails: 'close_table-of-content',
            engagementDestination: 'collapse_table-of-content',
          }
    );
  };
  return (
    <ListingDetailProvider data={{ listing }}>
      <ProductInfoCard
        isCypressTakingSnapshots={isCypressTakingSnapshots}
        listingInfoData={listing}
        head={head}
        header={header}
        hideLastUpdated={true}
      />
      <SplashElement />
      <SoftwareApplicationLD listing={listing} />
      <OneTapScript user={user} />
      <div>
        <Container fixedWidth={true} className={classes.pageContentContainer}>
          {!isDesktop && (
            <RichContentTitle
              richContent={listingDetailRichContent.richContent}
            />
          )}
          <Grid container={true} justifyContent="center" spacing={3}>
            {isDesktop ? (
              <Grid item={true} md={3} className={classes.stickyContainer}>
                <div ref={stickyContainerRef}>
                  <div>
                    <TableOfContent
                      content={listingDetailRichContent.richContent.content.filter(
                        ({ id }) => id
                      )}
                      stickyContainerRef={stickyContainerRef}
                    />
                  </div>
                </div>
              </Grid>
            ) : (
              <Grid item={true} xs={12}>
                <Paper>
                  <Accordion
                    onExpandClick={handleTableOfContentExpand}
                    summary={
                      <Typography variant="heading-sm">
                        Table of contents
                      </Typography>
                    }
                  >
                    <TableOfContent
                      content={listingDetailRichContent.richContent.content.filter(
                        ({ id }) => id
                      )}
                    />
                  </Accordion>
                </Paper>
              </Grid>
            )}
            <Grid item={true} xs={12} md={9}>
              {isDesktop && (
                <RichContentTitle
                  richContent={listingDetailRichContent.richContent}
                />
              )}

              <RichContent
                richContent={listingDetailRichContent.richContent}
                bxListing={listingDetailRichContent.bxApiData.listing}
                articles={listingDetailRichContent.resourcesData.articles}
                alternatives={listingDetailRichContent.bxApiData.alternatives}
                authors={listingDetailRichContent.resourcesData.authors}
                afterFirstItem={
                  listing.g2ProductSlug && (
                    <div className={classes.manageListingAfterFirstItem}>
                      <ManageProductListing
                        productName={listing.name}
                        g2ProductSlug={listing.g2ProductSlug}
                        engagementComponent="product_overview"
                      />
                    </div>
                  )
                }
              />
            </Grid>
          </Grid>
        </Container>
        {!isDesktop && <BackToTop className={classes.backToTop} />}
      </div>
    </ListingDetailProvider>
  );
};

export default ListingDetailRichContent;
